import { Badge, EditRemoveActions, monthOnly, type DataGridConfig } from '@eo/shared';

import type {
  AssignmentItem,
  AttributedList,
  BudgetHistoryRow,
  SupplementItem,
} from '../api/types';
import { formatHours } from '../helpers';

// How each list reached this client: a tag or the client's own name is solid;
// "fuzzy" is a guess from a partial name and worth a look.
export function makeAttributedListsGridConfig(): DataGridConfig<AttributedList> {
  return {
    getRowId: (row) => row.listName,
    paramPrefix: 'att_',
    rowTestId: 'attributed-list-row',
    pagination: false,
    emptyState: { title: 'No ClickUp hours reached this client this month.' },
    columns: [
      { id: 'listName', header: 'ClickUp list or tag', accessor: (row) => row.listName },
      {
        id: 'status',
        header: 'Matched by',
        accessor: (row) => row.status,
        cell: (row) => (
          <Badge variant={row.status === 'fuzzy' ? 'warning' : 'outline'}>{row.status}</Badge>
        ),
      },
      {
        id: 'totalHours',
        header: 'Hours',
        align: 'right',
        accessor: (row) => row.totalHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.totalHours)}</span>,
      },
    ],
  };
}

export function makeBudgetHistoryGridConfig(): DataGridConfig<BudgetHistoryRow> {
  return {
    getRowId: (row) => row.effectiveMonth,
    paramPrefix: 'bh_',
    rowTestId: 'budget-history-row',
    pagination: false,
    emptyState: {
      title: 'No budget set yet.',
      description: 'Set one and it applies from the shown month onward.',
    },
    columns: [
      {
        id: 'effectiveMonth',
        header: 'In force from',
        accessor: (row) => row.effectiveMonth,
        cell: (row) => monthOnly(row.effectiveMonth),
      },
      {
        id: 'fixedHours',
        header: 'Fixed',
        align: 'right',
        accessor: (row) => row.fixedHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.fixedHours)}</span>,
      },
      {
        id: 'variableHours',
        header: 'Variable',
        align: 'right',
        accessor: (row) => row.variableHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.variableHours)}</span>,
      },
    ],
  };
}

type SupplementsGridArgs = { onRemove: (row: SupplementItem) => void };

export function makeSupplementsGridConfig({
  onRemove,
}: SupplementsGridArgs): DataGridConfig<SupplementItem> {
  return {
    getRowId: (row) => row.id,
    paramPrefix: 'sup_',
    rowTestId: 'supplement-row',
    pagination: false,
    emptyState: { title: 'No supplements on this client.' },
    columns: [
      { id: 'kind', header: 'Kind', accessor: (row) => row.kind },
      { id: 'bucket', header: 'Bucket', accessor: (row) => row.bucket },
      {
        id: 'hours',
        header: 'Hours',
        align: 'right',
        accessor: (row) => row.hours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.hours)}</span>,
      },
      {
        id: 'window',
        header: 'Window',
        accessor: (row) => row.startDate,
        cell: (row) => `${row.startDate} → ${row.endDate}`,
      },
      { id: 'notes', header: 'Notes', accessor: (row) => row.notes },
      {
        id: 'actions',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <EditRemoveActions
            row={row}
            onRemove={onRemove}
            removeLabel={`Remove supplement of ${row.hours} hours`}
          />
        ),
      },
    ],
  };
}

type AssignmentsGridArgs = { onRemove: (row: AssignmentItem) => void };

export function makeAssignmentsGridConfig({
  onRemove,
}: AssignmentsGridArgs): DataGridConfig<AssignmentItem> {
  return {
    getRowId: (row) => row.id,
    paramPrefix: 'asg_',
    rowTestId: 'assignment-row',
    pagination: false,
    emptyState: {
      title: 'Nobody is assigned to this client.',
      description: 'Assign a member below to split the budget across the team.',
    },
    columns: [
      { id: 'name', header: 'Member', accessor: (row) => row.name },
      {
        id: 'fixedHours',
        header: 'Fixed',
        align: 'right',
        accessor: (row) => row.fixedHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.fixedHours)}</span>,
      },
      {
        id: 'variableHours',
        header: 'Variable',
        align: 'right',
        accessor: (row) => row.variableHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.variableHours)}</span>,
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <EditRemoveActions
            row={row}
            onRemove={onRemove}
            removeLabel={`Remove ${row.name} from this client`}
          />
        ),
      },
    ],
  };
}
